import { useState } from "react";
import { motion } from "framer-motion";

const nodes = [
  { id: "camera", x: 5, y: 42, label: "ANPR Camera Network", sub: "Police-deployed city cameras", icon: "cam", color: "#22d3ee" },
  { id: "ingest", x: 24, y: 42, label: "Video Ingestion", sub: "Real-time stream", icon: "stream", color: "#3b82f6" },
  { id: "detect", x: 43, y: 42, label: "Face Detection", sub: "AI-powered extraction", icon: "face", color: "#8b5cf6" },
  { id: "recognize", x: 62, y: 42, label: "Face Recognition", sub: "Deep learning match", icon: "brain", color: "#f59e0b" },
  { id: "db", x: 62, y: 12, label: "POI Database", sub: "Watchlist & records", icon: "db", color: "#64748b" },
  { id: "score", x: 80, y: 42, label: "Match Score", sub: "Confidence %", icon: "score", color: "#f472b6" },
  { id: "auto", x: 90, y: 20, label: "Auto Alert & Geo-Tag", sub: "Score >= 80%", icon: "alert", color: "#22c55e" },
  { id: "manual", x: 90, y: 65, label: "Manual Review", sub: "Score < 80%", icon: "manual", color: "#ef4444" },
  { id: "cnc", x: 75, y: 82, label: "Command & Control", sub: "Central dashboard", icon: "control", color: "#06b6d4" },
  { id: "track", x: 92, y: 82, label: "Tracking & Response", sub: "Field deployment", icon: "track", color: "#10b981" },
];

const edges = [
  { from: "camera", to: "ingest" }, { from: "ingest", to: "detect" },
  { from: "detect", to: "recognize" }, { from: "db", to: "recognize" },
  { from: "recognize", to: "score" }, { from: "score", to: "auto" },
  { from: "score", to: "manual" }, { from: "auto", to: "cnc" },
  { from: "manual", to: "cnc" }, { from: "cnc", to: "track" },
];

const nodeDetails = {
  camera: "Utilizes existing ANPR (Automatic Number Plate Recognition) cameras deployed across the city by police. No new hardware required — leverages the city-wide police surveillance camera network for facial recognition.",
  ingest: "Real-time video stream ingestion at 25-30 FPS. Supports RTSP/ONVIF protocols. Edge processing for bandwidth optimization with selective frame forwarding.",
  detect: "MTCNN/RetinaFace detector identifies and extracts faces from video frames. Handles multiple faces per frame, varying angles (up to 45 degrees), and partial occlusions.",
  recognize: "ArcFace/FaceNet deep learning model generates 512-dimensional face embeddings. Compares against POI database using cosine similarity for robust matching even with appearance changes.",
  db: "Centralized Person of Interest database with face embeddings, metadata, and watchlist categories. Supports real-time updates and bulk imports from law enforcement databases.",
  score: "Confidence score calculated from cosine similarity. Threshold at 80% determines automated vs manual workflow routing. Accounts for image quality, angle, and occlusion factors.",
  auto: "Scores >= 80%: Automatic geo-tagged alert sent to Command & Control Centre. Includes camera location, timestamp, confidence score, and POI identity. Real-time GPS tracking initiated.",
  manual: "Scores < 80%: Flagged for human operator review. Operator receives side-by-side comparison, confidence breakdown, and can confirm/reject match. Decision feeds back into model training.",
  cnc: "Centralized Command & Control dashboard with real-time alert feed, geo-spatial map view, operator workstations, and escalation protocols. Full audit trail and evidence chain.",
  track: "Upon confirmed match, automated geo-fence alerts across connected camera network. Vehicle and pedestrian tracking using multi-camera re-identification. Field team dispatch integration.",
};

function getNodePos(id) {
  const n = nodes.find(n => n.id === id);
  return n ? { x: n.x, y: n.y } : { x: 0, y: 0 };
}

export default function ArchitectureDiagram() {
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const active = selectedNode || hoveredNode;

  return (
    <div data-testid="architecture-diagram">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Scalable Architecture — AI Facial Recognition
        </h2>
        <p className="text-sm text-slate-400 mt-1">ANPR Camera Integration with Command & Control Centre</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Diagram */}
        <div className="xl:col-span-2 bg-slate-900/40 border border-slate-800 rounded-2xl p-6 relative overflow-hidden min-h-[480px]">
          <svg viewBox="0 0 100 100" className="w-full h-full absolute inset-0" style={{ minHeight: 480 }}>
            {/* Edges */}
            {edges.map((e, i) => {
              const from = getNodePos(e.from);
              const to = getNodePos(e.to);
              const isHighlighted = active === e.from || active === e.to;
              return (
                <g key={i}>
                  <line x1={from.x + 4} y1={from.y + 2} x2={to.x + 2} y2={to.y + 2}
                    stroke={isHighlighted ? "#22d3ee" : "#1e293b"} strokeWidth={isHighlighted ? 0.4 : 0.2}
                    strokeDasharray={e.from === "score" ? "1 0.5" : "none"} />
                  {/* Animated dot */}
                  <circle r="0.5" fill="#22d3ee" opacity={isHighlighted ? 0.8 : 0.3}>
                    <animateMotion dur={`${2 + i * 0.3}s`} repeatCount="indefinite"
                      path={`M${from.x + 4},${from.y + 2} L${to.x + 2},${to.y + 2}`} />
                  </circle>
                </g>
              );
            })}

            {/* Decision diamond at score */}
            <polygon points="84,42 86,40 88,42 86,44" fill="none" stroke="#f472b6" strokeWidth="0.3" opacity="0.5" />
            <text x="86" y="38" textAnchor="middle" fill="#f472b6" fontSize="1.8" fontWeight="bold">?</text>

            {/* Flow labels */}
            <text x="91" y="30" textAnchor="middle" fill="#22c55e" fontSize="1.6" fontWeight="bold">&#8805; 80%</text>
            <text x="91" y="60" textAnchor="middle" fill="#ef4444" fontSize="1.6" fontWeight="bold">&lt; 80%</text>
          </svg>

          {/* Node overlays */}
          {nodes.map(n => (
            <motion.div
              key={n.id}
              className={`absolute cursor-pointer transition-all duration-300 ${active === n.id ? 'z-20' : 'z-10'}`}
              style={{ left: `${n.x}%`, top: `${n.y}%`, transform: 'translate(-10%, -50%)' }}
              onMouseEnter={() => setHoveredNode(n.id)}
              onMouseLeave={() => setHoveredNode(null)}
              onClick={() => setSelectedNode(selectedNode === n.id ? null : n.id)}
              whileHover={{ scale: 1.08 }}
              data-testid={`node-${n.id}`}
            >
              <div className={`px-2.5 py-1.5 rounded-lg border text-center min-w-[80px] transition-all duration-300 ${
                active === n.id
                  ? 'bg-slate-800 border-cyan-500/50 shadow-[0_0_20px_rgba(6,182,212,0.2)]'
                  : 'bg-slate-900/80 border-slate-700/50'
              }`}>
                <div className="w-2 h-2 rounded-full mx-auto mb-1" style={{ backgroundColor: n.color }} />
                <p className="text-[8px] font-semibold text-white leading-tight">{n.label}</p>
                <p className="text-[6px] text-slate-500 mt-0.5">{n.sub}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Detail Panel */}
        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6" data-testid="detail-panel">
          <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {active ? nodes.find(n => n.id === active)?.label : "Component Details"}
          </h3>
          {active ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} key={active}>
              <div className="w-3 h-3 rounded-full mb-3" style={{ backgroundColor: nodes.find(n => n.id === active)?.color }} />
              <p className="text-sm text-slate-300 leading-relaxed">{nodeDetails[active]}</p>
              <div className="mt-4 pt-4 border-t border-slate-800">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Connected To</p>
                <div className="flex flex-wrap gap-1.5">
                  {edges.filter(e => e.from === active || e.to === active).map((e, i) => {
                    const connected = e.from === active ? e.to : e.from;
                    const cNode = nodes.find(n => n.id === connected);
                    return (
                      <span key={i} className="text-[10px] px-2 py-1 rounded-md bg-slate-800 text-slate-400 cursor-pointer hover:text-cyan-400 transition-colors"
                        onClick={() => setSelectedNode(connected)}>
                        {cNode?.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="text-center py-12">
              <p className="text-sm text-slate-500">Click or hover on any component to view details</p>
              <div className="mt-6 space-y-2">
                {[
                  { color: "#22c55e", label: "Auto workflow (>= 80% match)" },
                  { color: "#ef4444", label: "Manual review (< 80% match)" },
                  { color: "#22d3ee", label: "Data flow direction" },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-2 justify-center">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
                    <span className="text-xs text-slate-500">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Process flow summary */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5" data-testid="scenario-auto">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <h4 className="text-sm font-semibold text-emerald-400">Scenario 1: Automatic Response (Match >= 80%)</h4>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            ANPR Camera captures face → AI extracts & matches against POI database → Confidence score >= 80% → 
            Automatic geo-tagged alert to Command Centre → Real-time GPS tracking initiated → Field response deployed
          </p>
        </div>
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5" data-testid="scenario-manual">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <h4 className="text-sm font-semibold text-red-400">Scenario 2: Manual Review (Match &lt; 80%)</h4>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            ANPR Camera captures face → AI extracts & matches → Confidence score &lt; 80% (e.g., sunglasses, disguise) → 
            Flagged for human operator → Side-by-side comparison → Operator confirms/rejects → Decision logged
          </p>
        </div>
      </div>
    </div>
  );
}
